<script>
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { messageStamp } from 'shared/helpers/timeHelper';

export default {
  components: {
    UserAvatarWithName,
    InboxName,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    isOngoingType: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const { formatMessage } = useMessageFormatter();
    return {
      formatMessage,
    };
  },
  computed: {
    campaignStatus() {
      if (this.isTemplateCampaign) {
        const workflow = (this.campaign.workflow_state || 'draft').toUpperCase();
        const key = `CAMPAIGN.TEMPLATE.STATUS.${workflow}`;
        if (workflow === 'SCHEDULED') {
          return this.scheduledStatusLabel;
        }
        if (this.$te(key)) {
          return this.$t(key);
        }
        return workflow.toLowerCase();
      }
      if (this.isOngoingType) {
        return this.campaign.enabled
          ? this.$t('CAMPAIGN.LIST.STATUS.ENABLED')
          : this.$t('CAMPAIGN.LIST.STATUS.DISABLED');
      }

      return this.campaign.campaign_status === 'completed'
        ? this.$t('CAMPAIGN.LIST.STATUS.COMPLETED')
        : this.$t('CAMPAIGN.LIST.STATUS.ACTIVE');
    },
    colorScheme() {
      if (this.isTemplateCampaign) {
        switch (this.campaign.workflow_state) {
          case 'running':
            return 'success';
          case 'paused':
            return 'warning';
          case 'cancelled':
          case 'completed':
            return 'secondary';
          default:
            return 'info';
        }
      }
      if (this.isOngoingType) {
        return this.campaign.enabled ? 'success' : 'secondary';
      }
      return this.campaign.campaign_status === 'completed'
        ? 'secondary'
        : 'success';
    },
    isTemplateCampaign() {
      return !this.isOngoingType && !!this.campaign.template_provider;
    },
    templateDetails() {
      if (!this.isTemplateCampaign) {
        return null;
      }
      return {
        name: this.campaign.template_name,
        language: this.campaign.template_language,
        category: this.campaign.template_category,
        provider: this.campaign.template_provider,
      };
    },
    templateMetrics() {
      if (!this.isTemplateCampaign) {
        return [];
      }
      const metrics = this.campaign.template_metadata?.metrics || {};
      const map = [
        { key: 'queued', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.QUEUED') },
        { key: 'sending', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.SENDING') },
        { key: 'sent', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.SENT') },
        { key: 'delivered', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.DELIVERED') },
        { key: 'read', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.READ') },
        { key: 'failed', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.FAILED') },
        { key: 'skipped', label: this.$t('CAMPAIGN.TEMPLATE.METRICS.SKIPPED') },
      ];
      return map
        .map(item => ({ ...item, value: metrics[item.key] || 0 }))
        .filter(item => item.value || ['queued', 'sending', 'sent', 'delivered', 'read', 'failed'].includes(item.key));
    },
    showPauseButton() {
      return (
        this.isTemplateCampaign &&
        ['running', 'scheduled'].includes(this.campaign.workflow_state)
      );
    },
    showResumeButton() {
      return (
        this.isTemplateCampaign && this.campaign.workflow_state === 'paused'
      );
    },
    showCancelButton() {
      if (!this.isTemplateCampaign) return false;
      return !['cancelled', 'completed'].includes(this.campaign.workflow_state);
    },
    scheduledStatusLabel() {
      const baseLabel = this.$te('CAMPAIGN.TEMPLATE.STATUS.SCHEDULED')
        ? this.$t('CAMPAIGN.TEMPLATE.STATUS.SCHEDULED')
        : 'Scheduled';
      if (!this.campaign.scheduled_at) {
        return baseLabel;
      }

      return `${baseLabel} - ${messageStamp(
        new Date(this.campaign.scheduled_at),
        'LLL d, h:mm a'
      )}`;
    },
  },
  methods: {
    messageStamp,
  },
};
</script>

<template>
  <div
    class="px-5 py-4 mb-2 bg-white border rounded-md dark:bg-slate-800 border-slate-50 dark:border-slate-900"
  >
    <div class="flex flex-row items-start justify-between">
      <div class="flex flex-col">
        <div
          class="mb-1 -mt-1 text-base font-medium text-slate-900 dark:text-slate-100"
        >
          {{ campaign.title }}
        </div>
        <div
          v-if="isTemplateCampaign && templateDetails"
          class="text-sm text-slate-600 dark:text-slate-300"
        >
          {{ templateDetails.name }} · {{ templateDetails.language }} ·
          {{ templateDetails.category }}
        </div>
        <div
          v-else
          v-dompurify-html="formatMessage(campaign.message)"
          class="text-sm line-clamp-1 [&>p]:mb-0"
        />
      </div>
      <div class="flex flex-row flex-wrap items-center justify-end gap-1 shrink-0">
        <template v-if="isTemplateCampaign">
          <woot-button
            v-tooltip.top-end="$t('CAMPAIGN.LIST.BUTTONS.VIEW')"
            type="button"
            variant="hollow"
            icon="eye-show"
            size="small"
            color-scheme="secondary"
            :title="$t('CAMPAIGN.LIST.BUTTONS.VIEW')"
            @click="$emit('view', campaign)"
          />
          <woot-button
            v-if="showPauseButton"
            v-tooltip.top-end="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.PAUSE')"
            type="button"
            variant="hollow"
            icon="pause-circle"
            size="small"
            color-scheme="warning"
            :title="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.PAUSE')"
            @click="$emit('pause', campaign)"
          />
          <woot-button
            v-if="showResumeButton"
            v-tooltip.top-end="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.RESUME')"
            type="button"
            variant="hollow"
            icon="play-circle"
            size="small"
            color-scheme="success"
            :title="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.RESUME')"
            @click="$emit('resume', campaign)"
          />
          <woot-button
            v-if="showCancelButton"
            v-tooltip.top-end="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.CANCEL')"
            type="button"
            variant="hollow"
            icon="dismiss-circle"
            size="small"
            color-scheme="alert"
            :title="$t('CAMPAIGN.ADD.TEMPLATE.ACTIONS.CANCEL')"
            @click="$emit('cancel', campaign)"
          />
          <woot-button
            v-tooltip.top-end="$t('CAMPAIGN.LIST.BUTTONS.DELETE')"
            type="button"
            variant="hollow"
            icon="delete"
            size="small"
            color-scheme="alert"
            :title="$t('CAMPAIGN.LIST.BUTTONS.DELETE')"
            @click="$emit('delete', campaign)"
          />
        </template>
        <template v-else>
          <woot-button
            v-tooltip.top-end="$t('CAMPAIGN.LIST.BUTTONS.VIEW')"
            type="button"
            variant="hollow"
            icon="eye-show"
            color-scheme="secondary"
            size="small"
            :title="$t('CAMPAIGN.LIST.BUTTONS.VIEW')"
            @click="$emit('view', campaign)"
          />
          <woot-button
            v-tooltip.top-end="$t('CAMPAIGN.LIST.BUTTONS.DELETE')"
            type="button"
            variant="hollow"
            icon="delete"
            size="small"
            color-scheme="alert"
            :title="$t('CAMPAIGN.LIST.BUTTONS.DELETE')"
            @click="$emit('delete', campaign)"
          />
        </template>
      </div>
    </div>

    <div class="flex flex-row items-center mt-5 space-x-3">
      <woot-label
        small
        :title="campaignStatus"
        :color-scheme="colorScheme"
        class="mr-3 text-xs"
      />
      <InboxName :inbox="campaign.inbox" class="mb-1 ltr:ml-0 rtl:mr-0" />
      <UserAvatarWithName
        v-if="campaign.sender && !isTemplateCampaign"
        :user="campaign.sender"
        class="mb-1"
      />
      <div
        v-if="campaign.trigger_rules && campaign.trigger_rules.url && !isTemplateCampaign"
        class="w-1/4 mb-1 text-xs text-woot-600 text-truncate"
      >
        {{ campaign.trigger_rules.url }}
      </div>
      <div
        v-if="campaign.scheduled_at && !isTemplateCampaign"
        class="mb-1 text-xs text-slate-700 dark:text-slate-500"
      >
        {{ messageStamp(new Date(campaign.scheduled_at), 'LLL d, h:mm a') }}
      </div>
    </div>

    <div v-if="templateMetrics.length" class="mt-4 flex flex-wrap gap-4">
      <div
        v-for="metric in templateMetrics"
        :key="metric.key"
        class="text-xs text-slate-600 dark:text-slate-300"
      >
        <span class="font-semibold text-slate-800 dark:text-slate-100">
          {{ metric.value }}
        </span>
        {{ metric.label }}
      </div>
    </div>
  </div>
</template>
